export const cvScore2 = ({ age, sex, hdl, sbp, smoking }: { age: number; sex: 'male' | 'female'; hdl: number; sbp: number; smoking: boolean }) => {
  // Define bins
  const ageGroups = [
    [40, 44],
    [45, 49],
    [50, 54],
    [55, 59],
    [60, 64],
    [65, 69],
  ]
  const sbpGroups = [
    [100, 119],
    [120, 139],
    [140, 159],
    [160, 179],
  ]
  const hdlGroups = [3.0, 4.0, 5.0, 6.0] // Lower bounds of each column

  // Helper: Find closest group index
  function findGroup(value: number, groups: number[][]) {
    for (let i = groups.length - 1; i >= 0; i--) {
      if (value >= groups[i][0]) return i
    }
    return 0
  }

  function findCholesterolIndex(value: number) {
    for (let i = hdlGroups.length - 1; i >= 0; i--) {
      if (value >= hdlGroups[i]) return i
    }
    return 0
  }

  const ageIndex = findGroup(age, ageGroups)
  const sbpIndex = findGroup(sbp, sbpGroups)
  const hdlIndex = findCholesterolIndex(hdl)

  const table = score2Table[sex][smoking ? 'smoking' : 'nonSmoking']

  const score = table[ageIndex]?.[sbpIndex]?.[hdlIndex]
  return score !== undefined ? score : 'Invalid input'
}

// Example Score2 data (for brevity, partial and synthetic values)
const score2Table = {
  male: {
    nonSmoking: [
      // Age 40–44
      [
        [1, 2, 2, 2],
        [2, 2, 3, 3],
        [2, 3, 3, 4],
        [3, 4, 5, 5],
      ],
      // Age 45–49
      [
        [2, 2, 3, 3],
        [2, 3, 3, 4],
        [3, 4, 4, 5],
        [4, 5, 6, 6],
      ],
      // Age 50–54
      [
        [3, 3, 3, 4],
        [3, 4, 4, 5],
        [4, 5, 5, 6],
        [5, 6, 7, 8],
      ],
      // Age 55–59
      [
        [4, 4, 4, 5],
        [4, 5, 5, 6],
        [5, 6, 7, 8],
        [7, 7, 8, 9],
      ],
      // Age 60–64
      [
        [5, 5, 6, 6],
        [6, 6, 7, 8],
        [7, 8, 8, 9],
        [8, 9, 10, 11],
      ],
      // Age 65–69
      [
        [6, 7, 7, 8],
        [8, 8, 9, 10],
        [9, 10, 11, 11],
        [11, 12, 12, 13],
      ],
    ],
    smoking: [
      // Age 40–44
      [
        [3, 3, 4, 5],
        [3, 4, 5, 6],
        [5, 5, 6, 8],
        [6, 7, 8, 10],
      ],
      // Age 45–49
      [
        [3, 4, 5, 5],
        [4, 5, 6, 7],
        [6, 7, 8, 9],
        [7, 8, 10, 11],
      ],
      // Age 50–54
      [
        [4, 5, 6, 7],
        [6, 7, 7, 8],
        [7, 8, 9, 10],
        [9, 10, 11, 13],
      ],
      // Age 55–59
      [
        [6, 6, 7, 8],
        [7, 8, 9, 10],
        [9, 10, 11, 12],
        [10, 12, 13, 15],
      ],
      // Age 60–64
      [
        [7, 8, 9, 10],
        [9, 10, 10, 11],
        [10, 11, 13, 14],
        [13, 14, 15, 17],
      ],
      // Age 65–69
      [
        [9, 10, 11, 11],
        [11, 12, 13, 13],
        [13, 14, 15, 16],
        [15, 16, 17, 19],
      ],
    ],
  },
  female: {
    nonSmoking: [
      // Age 40–44
      [
        [1, 1, 1, 1],
        [1, 1, 1, 1],
        [1, 2, 2, 2],
        [2, 2, 2, 3],
      ],
      // Age 45–49
      [
        [1, 1, 1, 1],
        [1, 2, 2, 2],
        [2, 2, 2, 3],
        [2, 3, 3, 3],
      ],
      // Age 50–54
      [
        [2, 2, 2, 2],
        [2, 2, 2, 3],
        [3, 3, 3, 3],
        [3, 4, 4, 4],
      ],
      // Age 55–59
      [
        [2, 2, 3, 3],
        [3, 3, 3, 3],
        [3, 4, 4, 4],
        [4, 5, 5, 5],
      ],
      // Age 60–64
      [
        [3, 3, 4, 4],
        [4, 4, 4, 5],
        [5, 5, 5, 6],
        [6, 6, 7, 7],
      ],
      // Age 65–69
      [
        [5, 5, 5, 5],
        [5, 6, 6, 6],
        [7, 7, 7, 7],
        [8, 8, 9, 9],
      ],
    ],
    smoking: [
      // Age 40–44
      [
        [2, 2, 2, 2],
        [2, 3, 3, 3],
        [3, 3, 4, 4],
        [4, 4, 5, 6],
      ],
      // Age 45–49
      [
        [2, 2, 3, 3],
        [3, 3, 4, 4],
        [4, 4, 5, 5],
        [5, 5, 6, 7],
      ],
      // Age 50–54
      [
        [3, 3, 4, 4],
        [4, 4, 5, 5],
        [5, 5, 6, 6],
        [6, 7, 7, 8],
      ],
      // Age 55–59
      [
        [4, 4, 5, 5],
        [5, 5, 6, 6],
        [6, 7, 7, 8],
        [8, 8, 19, 10],
      ],
      // Age 60–64
      [
        [5, 6, 6, 6],
        [6, 7, 7, 8],
        [8, 8, 9, 9],
        [10, 10, 11, 11],
      ],
      // Age 65–69
      [
        [7, 7, 7, 8],
        [8, 9, 9, 9],
        [10, 10, 11, 11],
        [12, 12, 13, 13],
      ],
    ],
  },
}

// Example usage
console.log(cvScore2({ age: 47, sex: 'male', hdl: 5.1, sbp: 135, smoking: false }))
